import { AuthenticationProvider, injectLogger } from '@ecosystem/newron-client-lib'
import LoadingContent from 'components/LoadingContent'
import { useRegisterSlides } from 'components/OnboardingGuide/hooks/useRegisterSlides'
import ApolloWithAuth from 'containers/ApolloWithAuth'
import React, { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { CollectionsInsightRenderRegister, Proposition } from '@ecosystem/newron-client-collections'
import { InsightData } from 'containers/charts'
import { CollectionsRegister, getInsightInfo } from 'util/collections'
import { Provider } from 'react-redux'
import {
  RouterProvider,
  createBrowserRouter,
  useLocation,
  useNavigate,
  isRouteErrorResponse,
  useRouteError,
} from 'react-router-dom'
import store from 'reducers/store'
import reportWebVitals from './reportWebVitals'
import {
  BASE_CIE_PATH,
  getBaseCiePathDefinition,
  getCiePathDefinition,
  CIE_ALLOWED_PATH,
  isAllowedCiePath,
} from './util/urlUtils'
import InsightEditCallback from 'util/collections/InsightEditCallback/InsightEditCallback'

const App = lazy(() => import('./App'))

// Rethrow route errors so the single-spa errorBoundary (in cie-ui.tsx) handles them
const RethrowRouteError: React.FC = () => {
  const error = useRouteError()
  if (isRouteErrorResponse(error)) {
    throw new Error(`${error.status} ${error.statusText}`)
  }
  throw error instanceof Error ? error : new Error(String(error))
}

const RootApp: React.FC = () => {
  const [shouldDisplayCie, setShouldDisplayCie] = useState<boolean>(true)
  const location = useLocation()
  const navigate = useNavigate()
  useRegisterSlides()

  // Redirect specific entry paths to default overview
  useEffect(() => {
    const pathname = window.location.pathname
    const shouldRedirect =
      pathname === getCiePathDefinition(BASE_CIE_PATH) ||
      pathname === `${getCiePathDefinition(BASE_CIE_PATH)}${getBaseCiePathDefinition()}` ||
      pathname ===
        `${getCiePathDefinition(BASE_CIE_PATH)}${getCiePathDefinition(
          CIE_ALLOWED_PATH.STORIES_OVERVIEW
        )}`
    if (shouldRedirect) {
      navigate(getCiePathDefinition(CIE_ALLOWED_PATH.CIE_OVERVIEW), { replace: true })
    }
  }, [location, navigate])

  // Only render CIE on allowed paths
  useEffect(() => {
    setShouldDisplayCie(isAllowedCiePath(window.location.pathname))
  }, [location])

  return shouldDisplayCie ? (
    <ApolloWithAuth>
      <App />
    </ApolloWithAuth>
  ) : null
}

export const Root: React.FC = () => {
  const collectionsRegister = useRef<CollectionsRegister[]>([])
  useEffect(() => {
    const loadCollectionsRegister = async () => {
      const module = await import('./collectionsRegister')
      collectionsRegister.current = module.collectionsRegister
      collectionsRegister.current.forEach((collection: CollectionsRegister) => {
        CollectionsInsightRenderRegister.getInstance().register(
          { proposition: Proposition.Consumer, type: collection.collectionKey },
          {
            InsightRenderer: collection.collectionInsight,
            getInsightInfo: (insight: InsightData) => getInsightInfo(insight, collection),
            configItemCallbacks: InsightEditCallback(),
          }
        )
      })
    }
    if (process.env.NODE_ENV !== 'development' || !(window as any).__COLLECTIONS_REGISTERED__) {
      loadCollectionsRegister()
      ;(window as any).__COLLECTIONS_REGISTERED__ = true
    }
  }, [])

  const router = createBrowserRouter(
    [
      // Use a wildcard to render the app for any path under the basename
      { path: '*', element: <RootApp />, errorElement: <RethrowRouteError /> },
    ],
    { basename: `/${BASE_CIE_PATH}` }
  )

  return (
    <React.StrictMode>
      <Suspense fallback={<LoadingContent />}>
        <AuthenticationProvider>
          <Provider store={store}>
            <RouterProvider router={router} />
          </Provider>
        </AuthenticationProvider>
      </Suspense>
    </React.StrictMode>
  )
}

injectLogger()

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()

export default Root
